<template>
  <v-main>
    <v-container fluid class="pa-4">

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-16">
        <v-progress-circular indeterminate color="primary" size="52"></v-progress-circular>
        <div class="grey--text text-body-2 mt-3">Loading log details...</div>
      </div>

      <template v-else-if="log">

        <!-- Hero Header -->
        <div class="detail-hero mb-4" :class="heroClass">
          <div class="hero-inner">
            <!-- Back + Title -->
            <div class="d-flex align-center mb-3">
              <v-btn icon dark small class="mr-2 back-btn" @click="$router.back()">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <span class="white--text text-caption" style="opacity:0.7; letter-spacing:1px; text-transform:uppercase;">API Logs</span>
              <v-icon color="white" small class="mx-1" style="opacity:0.5;">mdi-chevron-right</v-icon>
              <span class="white--text text-caption" style="opacity:0.7;">Detail</span>
            </div>

            <!-- URL + Method row -->
            <div class="d-flex align-center flex-wrap" style="gap:10px;">
              <span class="method-hero-badge" :class="'method-' + (log.method || 'get').toLowerCase()">
                {{ log.method }}
              </span>
              <span class="white--text font-weight-bold" style="font-size:16px; word-break:break-all;">
                {{ urlPath(log.url) }}
              </span>
              <v-spacer></v-spacer>
              <div class="status-hero-badge" :class="statusHeroClass(log.response_status)">
                {{ log.response_status }}
              </div>
            </div>

            <!-- Meta strip -->
            <div class="d-flex align-center flex-wrap mt-3" style="gap:18px;">
              <div class="hero-meta-item">
                <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-identifier</v-icon>
                <span class="mono-text">{{ log.request_id ? log.request_id.substring(0, 16) + '...' : '-' }}</span>
              </div>
              <div class="hero-meta-item">
                <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-account</v-icon>
                <span>User {{ log.user_id || '—' }} / Co. {{ log.company_id || '—' }}</span>
              </div>
              <div class="hero-meta-item">
                <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-web</v-icon>
                <span>{{ log.ip_address || '—' }}</span>
              </div>
              <div class="hero-meta-item">
                <v-icon x-small color="rgba(255,255,255,0.6)" class="mr-1">mdi-clock-outline</v-icon>
                <span>{{ formatDate(log.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Performance Bar -->
        <v-card elevation="2" rounded="xl" class="mb-4">
          <v-card-text class="py-3 px-5">
            <v-row dense align="center">
              <v-col cols="auto">
                <div class="perf-metric" :class="log.duration_ms > 2000 ? 'perf-metric--red' : log.duration_ms > 500 ? 'perf-metric--orange' : 'perf-metric--green'">
                  <v-icon small class="mr-1">mdi-speedometer</v-icon>
                  <div>
                    <div class="perf-metric__value">{{ log.duration_ms }}ms</div>
                    <div class="perf-metric__label">Duration</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="auto"><div class="perf-divider"></div></v-col>
              <v-col cols="auto">
                <div class="perf-metric perf-metric--blue">
                  <v-icon small class="mr-1">mdi-memory</v-icon>
                  <div>
                    <div class="perf-metric__value">{{ log.memory_usage || 0 }} KB</div>
                    <div class="perf-metric__label">Peak Memory</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="auto"><div class="perf-divider"></div></v-col>
              <v-col cols="auto">
                <div class="perf-metric" :class="log.db_query_count > 50 ? 'perf-metric--red' : log.db_query_count > 20 ? 'perf-metric--orange' : 'perf-metric--purple'">
                  <v-icon small class="mr-1">mdi-database</v-icon>
                  <div>
                    <div class="perf-metric__value">{{ log.db_query_count || 0 }} queries</div>
                    <div class="perf-metric__label">DB Calls</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="auto"><div class="perf-divider"></div></v-col>
              <v-col cols="auto">
                <div class="perf-metric perf-metric--teal">
                  <v-icon small class="mr-1">mdi-timer-outline</v-icon>
                  <div>
                    <div class="perf-metric__value">{{ log.db_query_time_ms || 0 }}ms</div>
                    <div class="perf-metric__label">DB Time</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="auto"><div class="perf-divider"></div></v-col>
              <v-col cols="auto">
                <div class="perf-metric perf-metric--grey">
                  <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
                  <div>
                    <div class="perf-metric__value">{{ log.response_size ? (log.response_size / 1024).toFixed(2) + ' KB' : '—' }}</div>
                    <div class="perf-metric__label">Response Size</div>
                  </div>
                </div>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="auto">
                <div class="text-caption grey--text" v-if="log.controller_action">
                  <v-icon x-small class="mr-1">mdi-code-braces</v-icon>
                  <span class="mono-text">{{ shortController(log.controller_action) }}</span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Main Content: Request + Response -->
        <v-row>
          <!-- Request Column -->
          <v-col md="6">
            <v-card elevation="2" rounded="xl" class="detail-card mb-4">
              <div class="detail-card-header detail-card-header--request">
                <v-icon color="white" small class="mr-2">mdi-arrow-up-circle</v-icon>
                <span>Request</span>
                <v-spacer></v-spacer>
                <span class="method-hero-badge method-hero-badge--sm" :class="'method-' + (log.method || 'get').toLowerCase()">{{ log.method }}</span>
              </div>

              <v-card-text class="pa-4">
                <!-- Request ID -->
                <div class="info-row">
                  <span class="info-label">Request ID</span>
                  <div class="d-flex align-center" style="gap:6px;">
                    <span class="mono-chip">{{ log.request_id }}</span>
                    <v-btn icon x-small @click="copyText(log.request_id)">
                      <v-icon x-small color="grey">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- URL -->
                <div class="info-row">
                  <span class="info-label">Full URL</span>
                  <span class="mono-text" style="font-size:12px; word-break:break-all;">{{ log.url }}</span>
                </div>

                <!-- IP / UA -->
                <div class="info-row">
                  <span class="info-label">IP Address</span>
                  <span class="info-value">{{ log.ip_address || '—' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">User Agent</span>
                  <span class="info-value" style="font-size:11px; color:#777;">{{ log.user_agent || '—' }}</span>
                </div>

                <!-- User / Company -->
                <div class="info-row" style="border-bottom:none;">
                  <span class="info-label">User / Company</span>
                  <div class="d-flex align-center" style="gap:6px;">
                    <span v-if="log.user_id" class="pill-badge pill-badge--blue">
                      <v-icon x-small class="mr-1">mdi-account</v-icon>{{ log.user_id }}
                    </span>
                    <span v-if="log.company_id" class="pill-badge pill-badge--green">
                      <v-icon x-small class="mr-1">mdi-office-building</v-icon>{{ log.company_id }}
                    </span>
                  </div>
                </div>

                <!-- Request Body -->
                <div v-if="log.request_body" class="mt-3">
                  <div class="code-block-header">
                    <span><v-icon x-small class="mr-1">mdi-code-json</v-icon>Request Body</span>
                    <v-btn icon x-small @click="copyText(log.request_body)">
                      <v-icon x-small color="grey">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <div class="code-block">
                    <pre class="code-pre" v-html="syntaxHighlight(log.request_body)"></pre>
                  </div>
                </div>

                <!-- Request Headers (collapsed) -->
                <div v-if="log.request_headers" class="mt-3">
                  <div
                    class="collapsible-header"
                    @click="showHeaders = !showHeaders"
                  >
                    <span><v-icon x-small class="mr-1">mdi-format-list-bulleted</v-icon>Request Headers</span>
                    <v-icon small :class="showHeaders ? 'rotate-180' : ''">mdi-chevron-down</v-icon>
                  </div>
                  <div v-if="showHeaders" class="code-block mt-1">
                    <pre class="code-pre" v-html="syntaxHighlight(log.request_headers)"></pre>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Response Column -->
          <v-col md="6">
            <v-card elevation="2" rounded="xl" class="detail-card mb-4" :class="log.is_error ? 'detail-card--error-border' : ''">
              <div class="detail-card-header" :class="log.is_error ? 'detail-card-header--error' : 'detail-card-header--response'">
                <v-icon color="white" small class="mr-2">mdi-arrow-down-circle</v-icon>
                <span>Response</span>
                <v-spacer></v-spacer>
                <span class="status-hero-badge status-hero-badge--sm" :class="statusHeroClass(log.response_status)">
                  {{ log.response_status }}
                </span>
              </div>

              <v-card-text class="pa-4">
                <div class="info-row">
                  <span class="info-label">Status</span>
                  <div class="d-flex align-center" style="gap:8px;">
                    <span class="status-badge-lg" :class="statusHeroClass(log.response_status)">{{ log.response_status }}</span>
                    <span class="grey--text text-caption">{{ statusText(log.response_status) }}</span>
                  </div>
                </div>
                <div class="info-row" style="border-bottom:none;">
                  <span class="info-label">Response Size</span>
                  <span class="info-value">{{ log.response_size ? (log.response_size / 1024).toFixed(2) + ' KB' : '—' }}</span>
                </div>

                <!-- Response Body -->
                <div v-if="log.response_body" class="mt-3">
                  <div class="code-block-header">
                    <span><v-icon x-small class="mr-1">mdi-code-json</v-icon>Response Body</span>
                    <v-btn icon x-small @click="copyText(log.response_body)">
                      <v-icon x-small color="grey">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <div class="code-block" :class="log.is_error ? 'code-block--error' : ''">
                    <pre class="code-pre" v-html="syntaxHighlight(log.response_body)"></pre>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Linked Error Card -->
            <v-card v-if="log.error_log" elevation="2" rounded="xl" class="detail-card detail-card--error-border mb-4">
              <div class="detail-card-header detail-card-header--error">
                <v-icon color="white" small class="mr-2">mdi-alert-circle</v-icon>
                <span>Linked Error</span>
                <v-spacer></v-spacer>
                <span class="severity-badge" :class="'severity-' + log.error_log.severity">{{ log.error_log.severity }}</span>
              </div>
              <v-card-text class="pa-4">
                <div class="info-row">
                  <span class="info-label">Error Type</span>
                  <span class="mono-text" style="font-size:12px; color:#c62828;">{{ log.error_log.error_type }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Message</span>
                  <span class="info-value red--text text--darken-2">{{ log.error_log.message }}</span>
                </div>
                <div class="info-row" style="border-bottom:none;">
                  <span class="info-label">Location</span>
                  <span class="mono-text" style="font-size:11px; color:#888;">
                    {{ log.error_log.file ? log.error_log.file.split('/').pop() : '' }}:{{ log.error_log.line }}
                  </span>
                </div>
                <v-btn small color="red" dark rounded class="mt-3" :to="'/error-logs/' + log.error_log.id" elevation="0">
                  <v-icon small left>mdi-arrow-right-circle</v-icon>View Full Error
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Footer timestamp -->
        <div class="d-flex align-center mt-1 mb-2">
          <v-icon x-small color="grey" class="mr-1">mdi-clock-check-outline</v-icon>
          <span class="text-caption grey--text">Logged at {{ log.created_at }}</span>
        </div>

      </template>
    </v-container>

    <!-- Copy snackbar -->
    <v-snackbar v-model="snackbar" timeout="1500" bottom right color="grey darken-3" rounded="pill">
      <v-icon small class="mr-1">mdi-check-circle</v-icon> Copied to clipboard
    </v-snackbar>

  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "ApiLogIdPage",
  data() {
    return {
      log: null,
      isLoading: false,
      showHeaders: false,
      snackbar: false,
    };
  },
  mounted() {
    this.getData();
  },
  computed: {
    heroClass() {
      if (!this.log) return "hero--default";
      if (this.log.response_status >= 500) return "hero--error";
      if (this.log.response_status >= 400) return "hero--warning";
      return "hero--success";
    },
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(`/api_logs/${this.$route.params.id}`);
        this.log = data.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    statusHeroClass(status) {
      if (status >= 500) return "status-5xx";
      if (status >= 400) return "status-4xx";
      if (status >= 300) return "status-3xx";
      return "status-2xx";
    },
    statusText(status) {
      const map = {
        200: "OK", 201: "Created", 204: "No Content",
        301: "Moved Permanently", 302: "Found",
        400: "Bad Request", 401: "Unauthorized", 403: "Forbidden",
        404: "Not Found", 405: "Method Not Allowed", 422: "Unprocessable Entity", 429: "Too Many Requests",
        500: "Internal Server Error", 502: "Bad Gateway", 503: "Service Unavailable",
      };
      return map[status] || "";
    },
    urlPath(url) {
      try { return new URL(url).pathname; } catch { return url; }
    },
    shortController(action) {
      if (!action) return "";
      const parts = action.split("\\");
      return parts[parts.length - 1] || action;
    },
    prettyJson(str) {
      try { return JSON.stringify(JSON.parse(str), null, 2); } catch { return str; }
    },
    syntaxHighlight(str) {
      if (!str) return '';
      const raw = typeof str === 'string' ? str : JSON.stringify(str);

      // Handle truncated payloads — strip marker, parse what we have
      const TRUNC = '...[TRUNCATED]';
      const isTruncated = raw.endsWith(TRUNC);
      const parseTarget = isTruncated ? raw.slice(0, -TRUNC.length) : raw;

      let pretty;
      try {
        pretty = JSON.stringify(JSON.parse(parseTarget), null, 2);
        if (isTruncated) pretty += '\n' + TRUNC;
      } catch {
        // Not valid JSON even after stripping — use character-level formatter
        pretty = this.basicFormatJson(raw);
      }

      // Escape HTML entities, then colorize tokens
      const escaped = pretty
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      return escaped.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        (match) => {
          let cls = 'json-number';
          if (/^"/.test(match)) {
            cls = /:$/.test(match) ? 'json-key' : 'json-string';
          } else if (/true|false/.test(match)) {
            cls = 'json-boolean';
          } else if (/null/.test(match)) {
            cls = 'json-null';
          }
          return `<span class="${cls}">${match}</span>`;
        }
      );
    },
    basicFormatJson(str) {
      // Character-level formatter for minified/invalid JSON
      let indent = 0;
      let inString = false;
      let result = '';
      for (let i = 0; i < str.length; i++) {
        const ch = str[i];
        const prev = str[i - 1];
        if (ch === '"' && prev !== '\\') { inString = !inString; }
        if (inString) { result += ch; continue; }
        switch (ch) {
          case '{': case '[':
            result += ch + '\n' + '  '.repeat(++indent);
            break;
          case '}': case ']':
            result += '\n' + '  '.repeat(--indent) + ch;
            break;
          case ',':
            result += ch + '\n' + '  '.repeat(indent);
            break;
          case ':':
            result += ': ';
            break;
          default:
            result += ch;
        }
      }
      return result;
    },
    copyText(text) {
      navigator.clipboard.writeText(text).then(() => { this.snackbar = true; }).catch(() => {});
    },
    formatDate(dt) {
      if (!dt) return "-";
      return new Date(dt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "medium" });
    },
  },
};
</script>

<style scoped>
/* ===== Hero Header ===== */
.detail-hero {
  border-radius: 16px;
  padding: 22px 24px 18px;
}
.hero--success { background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #388e3c 100%); }
.hero--warning { background: linear-gradient(135deg, #bf360c 0%, #e64a19 50%, #f4511e 100%); }
.hero--error   { background: linear-gradient(135deg, #7f0000 0%, #b71c1c 50%, #c62828 100%); }
.hero--default { background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #1565c0 100%); }

.back-btn { background: rgba(255,255,255,0.15) !important; }

.method-hero-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
}
.method-hero-badge--sm { padding: 2px 9px; font-size: 11px; }
.method-get    { background: rgba(255,255,255,0.2); color: #ffffff; border: 1.5px solid rgba(255,255,255,0.4); }
.method-post   { background: rgba(255,255,255,0.2); color: #ffffff; border: 1.5px solid rgba(255,255,255,0.4); }
.method-patch  { background: rgba(255,255,255,0.2); color: #ffffff; border: 1.5px solid rgba(255,255,255,0.4); }
.method-put    { background: rgba(255,255,255,0.2); color: #ffffff; border: 1.5px solid rgba(255,255,255,0.4); }
.method-delete { background: rgba(255,255,255,0.2); color: #ffffff; border: 1.5px solid rgba(255,255,255,0.4); }

.status-hero-badge {
  display: inline-block;
  padding: 5px 14px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 800;
}
.status-hero-badge--sm { padding: 2px 9px; font-size: 11px; font-weight: 700; }
.status-2xx { background: rgba(255,255,255,0.25); color: #fff; border: 1.5px solid rgba(255,255,255,0.4); }
.status-3xx { background: rgba(255,255,255,0.25); color: #fff; border: 1.5px solid rgba(255,255,255,0.4); }
.status-4xx { background: rgba(255,255,255,0.25); color: #fff; border: 1.5px solid rgba(255,255,255,0.4); }
.status-5xx { background: rgba(255,255,255,0.25); color: #fff; border: 1.5px solid rgba(255,255,255,0.4); }

.hero-meta-item {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: rgba(255,255,255,0.75);
}

/* ===== Performance Bar ===== */
.perf-metric {
  display: flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 10px;
}
.perf-metric .v-icon { opacity: 0.8; }
.perf-metric__value { font-size: 14px; font-weight: 700; line-height: 1.2; }
.perf-metric__label { font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.4px; }
.perf-metric--green  { background: #e8f5e9; } .perf-metric--green .perf-metric__value  { color: #2e7d32; } .perf-metric--green  .v-icon { color: #2e7d32 !important; }
.perf-metric--orange { background: #fff3e0; } .perf-metric--orange .perf-metric__value { color: #e65100; } .perf-metric--orange .v-icon { color: #e65100 !important; }
.perf-metric--red    { background: #fce4ec; } .perf-metric--red .perf-metric__value    { color: #c62828; } .perf-metric--red    .v-icon { color: #c62828 !important; }
.perf-metric--blue   { background: #e3f2fd; } .perf-metric--blue .perf-metric__value   { color: #1565c0; } .perf-metric--blue   .v-icon { color: #1565c0 !important; }
.perf-metric--purple { background: #f3e5f5; } .perf-metric--purple .perf-metric__value { color: #6a1b9a; } .perf-metric--purple .v-icon { color: #6a1b9a !important; }
.perf-metric--teal   { background: #e0f2f1; } .perf-metric--teal .perf-metric__value   { color: #00695c; } .perf-metric--teal   .v-icon { color: #00695c !important; }
.perf-metric--grey   { background: #f5f5f5; } .perf-metric--grey .perf-metric__value   { color: #424242; } .perf-metric--grey   .v-icon { color: #424242 !important; }
.perf-divider { width: 1px; height: 28px; background: #e0e0e0; margin: 0 4px; }

/* ===== Detail Cards ===== */
.detail-card { overflow: hidden; }
.detail-card--error-border { border: 1.5px solid #ef9a9a !important; }

.detail-card-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: white;
}
.detail-card-header--request  { background: linear-gradient(90deg, #1565c0, #1976d2); }
.detail-card-header--response { background: linear-gradient(90deg, #2e7d32, #43a047); }
.detail-card-header--error    { background: linear-gradient(90deg, #b71c1c, #e53935); }

/* ===== Info Rows ===== */
.info-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 9px 0;
  border-bottom: 1px solid #f0f0f0;
  gap: 12px;
}
.info-label {
  font-size: 11px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  min-width: 100px;
  flex-shrink: 0;
  margin-top: 2px;
}
.info-value { font-size: 13px; color: #333; text-align: right; flex: 1; }

/* ===== Code Blocks ===== */
.code-block-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}
.code-block {
  background: #1e1e2e;
  border-radius: 10px;
  padding: 1px;
}
.code-block--error { background: #2d1b1b; }
.code-pre {
  background: transparent;
  color: #cdd6f4;
  font-family: 'Courier New', 'Monaco', monospace;
  font-size: 11.5px;
  line-height: 1.6;
  padding: 14px;
  overflow: auto;
  max-height: 280px;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
}

/* ===== Collapsible Headers ===== */
.collapsible-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  user-select: none;
  transition: background 0.15s;
}
.collapsible-header:hover { background: #eeeeee; }
.rotate-180 { transform: rotate(180deg); transition: transform 0.2s; }

/* ===== Chips / Badges ===== */
.mono-chip {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  background: #f0f0f0;
  color: #444;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.3px;
  word-break: break-all;
}
.mono-text {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #555;
}
.pill-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}
.pill-badge--blue  { background: #e3f2fd; color: #1565c0; }
.pill-badge--green { background: #e8f5e9; color: #2e7d32; }

.status-badge-lg {
  display: inline-block;
  padding: 4px 14px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}
/* reuse status colors from parent scope - define locally */
.status-badge-lg.status-2xx { background: #e8f5e9; color: #2e7d32; }
.status-badge-lg.status-3xx { background: #e3f2fd; color: #1565c0; }
.status-badge-lg.status-4xx { background: #fff3e0; color: #e65100; }
.status-badge-lg.status-5xx { background: #fce4ec; color: #c62828; }

.severity-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.severity-critical { background: rgba(255,255,255,0.25); color: white; }
.severity-error    { background: rgba(255,255,255,0.2);  color: white; }
.severity-warning  { background: rgba(255,255,255,0.2);  color: white; }

/* ===== JSON Syntax Highlighting (Catppuccin Mocha palette) ===== */
.code-pre :deep(.json-key)     { color: #89dceb; font-weight: 600; }  /* cyan    — object keys   */
.code-pre :deep(.json-string)  { color: #a6e3a1; }                    /* green   — string values */
.code-pre :deep(.json-number)  { color: #fab387; }                    /* peach   — numbers       */
.code-pre :deep(.json-boolean) { color: #cba6f7; font-weight: 700; }  /* mauve   — true / false  */
.code-pre :deep(.json-null)    { color: #f38ba8; font-weight: 700; }  /* red     — null          */
</style>
